import { useMemo } from 'react';
import { Cloud } from '@react-three/drei';

/**
 * Ring of low-poly mountain silhouettes around the scene perimeter.
 * Partially hidden by fog for atmospheric depth.
 */
function DistantMountains() {
  const mountains = useMemo(() => {
    const result: Array<{
      position: [number, number, number];
      rotation: [number, number, number];
      scale: [number, number, number];
      color: string;
    }> = [];

    const radius = 42;
    const count = 28;

    for (let i = 0; i < count; i++) {
      const angle = (i / count) * Math.PI * 2;
      // Slight radial variation so mountains aren't in a perfect circle
      const r = radius + (Math.sin(i * 3.7) * 4 + Math.cos(i * 5.1) * 3);
      const x = Math.cos(angle) * r;
      const z = Math.sin(angle) * r;

      // Vary height — some tall peaks, some rolling hills
      const height = 5 + Math.sin(i * 2.3) * 4 + Math.cos(i * 4.7) * 3;
      const width = 2.5 + Math.sin(i * 1.8) * 1.5;
      const depth = 1.5 + Math.cos(i * 3.1) * 1;

      // Dark silhouettes with subtle warm hints for sunset
      const colorChoice = i % 3;
      const color =
        colorChoice === 0
          ? '#3a2f28'
          : colorChoice === 1
            ? '#4a3830'
            : '#332820';

      result.push({
        position: [x, height / 2 - 1, z],
        rotation: [0, angle + Math.PI / 2, 0],
        scale: [width, height, depth],
        color,
      });
    }

    return result;
  }, []);

  return (
    <group>
      {mountains.map((m, i) => (
        <mesh
          key={`mtn-${i}`}
          position={m.position}
          rotation={m.rotation}
          scale={m.scale}
        >
          <coneGeometry args={[1, 1, 6, 4]} />
          <meshStandardMaterial
            color={m.color}
            roughness={1}
            flatShading
          />
        </mesh>
      ))}
    </group>
  );
}

/**
 * Scattered cloud groups at various heights throughout the sky.
 * Uses drei's Cloud shader for fluffy volumetric look.
 */
function CloudFields() {
  const clouds = useMemo(() => {
    const result: Array<{
      position: [number, number, number];
      scale: [number, number, number];
      opacity: number;
      speed: number;
    }> = [];

    // High cirrus clouds — scattered wide, thin
    for (let i = 0; i < 12; i++) {
      const angle = (i / 12) * Math.PI * 2 + (Math.random() - 0.5) * 0.5;
      const dist = 25 + Math.random() * 20;
      result.push({
        position: [
          Math.cos(angle) * dist,
          20 + Math.random() * 12,
          Math.sin(angle) * dist,
        ],
        scale: [8 + Math.random() * 12, 0.8 + Math.random() * 1.5, 4 + Math.random() * 6],
        opacity: 0.25 + Math.random() * 0.2,
        speed: 0.05 + Math.random() * 0.1,
      });
    }

    // Mid-level cumulus — fuller, closer together
    for (let i = 0; i < 10; i++) {
      const angle = (i / 10) * Math.PI * 2 + (Math.random() - 0.5) * 0.4;
      const dist = 22 + Math.random() * 18;
      result.push({
        position: [
          Math.cos(angle) * dist,
          12 + Math.random() * 8,
          Math.sin(angle) * dist,
        ],
        scale: [6 + Math.random() * 10, 1.5 + Math.random() * 3, 4 + Math.random() * 8],
        opacity: 0.35 + Math.random() * 0.25,
        speed: 0.08 + Math.random() * 0.12,
      });
    }

    // Low horizon clouds — catch the sunset colors
    for (let i = 0; i < 8; i++) {
      const angle = (i / 8) * Math.PI * 2 + (Math.random() - 0.5) * 0.3;
      const dist = 30 + Math.random() * 15;
      result.push({
        position: [
          Math.cos(angle) * dist,
          4 + Math.random() * 5,
          Math.sin(angle) * dist,
        ],
        scale: [10 + Math.random() * 15, 1 + Math.random() * 2, 6 + Math.random() * 8],
        opacity: 0.3 + Math.random() * 0.2,
        speed: 0.04 + Math.random() * 0.06,
      });
    }

    return result;
  }, []);

  return (
    <group>
      {clouds.map((c, i) => (
        <Cloud
          key={`cloud-${i}`}
          position={c.position}
          scale={c.scale}
          speed={c.speed}
          opacity={c.opacity}
          color="#ffd4b8"
          segments={20}
        />
      ))}
    </group>
  );
}

/**
 * Full skybox: distant mountains + cloud layers.
 * Place alongside <Sky> and <Stars> in the scene.
 */
export default function SkyBox() {
  return (
    <group>
      <DistantMountains />
      <CloudFields />
    </group>
  );
}
